import React, { useContext, useMemo } from "react";

import cx from "classnames";

import type { ActionItem } from "~/actionItems/types";
import { AppContext } from "~/common";
import { colors } from "~/ui";
import Avatar from "~/ui/legacy/Avatar";

import styles from "./ActionItemAssignees.module.css";

type ActionItemAssigneeProps = {
	assignee: NonNullable<ActionItem["assignments"]>[0]["assignee"];
	note?: ActionItem["note"];
};

function ActionItemAssignee({ assignee, note }: ActionItemAssigneeProps) {
	const assigneeIsExternal = useMemo(() => {
		if (!note || !("noteuserSet" in note)) return false;
		return !note.noteuserSet.find((nu: { user: { id: string } }) => nu.user.id === assignee.id);
	}, [assignee, note]);
	return <Avatar link={false} width="1.5em" key={assignee.id} user={assignee} dashedOutline={!!assigneeIsExternal} />;
}

type ActionItemAssigneesProps = {
	assignments: NonNullable<ActionItem["assignments"]>;
	note?: ActionItem["note"];
};

export default function ActionItemAssignees({ assignments, note }: ActionItemAssigneesProps) {
	const { user } = useContext(AppContext);
	const assigneesToRender = useMemo(() => {
		// We want to render the viewer first, so let's sort by that
		let result = [...assignments.map(assignment => assignment.assignee)];
		result.sort((a, _b) => (a.id === user?.id ? -1 : 0));
		// We want to render 3 assignees max on desktop
		// The list uses row-reverse, so to keep our order let's reverse the list
		// TODO: Change based on the screen's width
		result = result.slice(0, assignments.length <= 3 ? 3 : 2);
		result.reverse();
		return result;
	}, [user, assignments]);

	return (
		<div className={cx("assignee-avatars", styles.assignees)}>
			{assignments.length > assigneesToRender.length && (
				<Avatar
					user={{
						id: "",
					}}
					width="1.5em"
					initials={assignments.length - assigneesToRender.length}
					uiAvatarOptions={{
						background: colors.grey[200].substring(1),
						color: colors.grey[600].substring(1),
					}}
				/>
			)}
			{assigneesToRender.map(assignee => (
				<ActionItemAssignee key={assignee.id} assignee={assignee} note={note} />
			))}
		</div>
	);
}
